<script setup>
const { isMini } = defineProps({
  isMini: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const emits = defineEmits(["signup", "onClose"]);

const route = useRoute();
const config = useRuntimeConfig();
const { getSession } = useAuth();
const { setToken } = useAuthState();
const toast = useToast();

const email = ref("");
const password = ref("");
const isLoading = ref(false);
const isInvalid = ref(false);
const socialLoginLoading = ref(false);

const handleLogin = async () => {
  isInvalid.value = false;
  isLoading.value = true;

  await useApiFetch("auth/login", {
    method: "POST",
    body: {
      email: email.value,
      password: password.value,
    },
    async onResponse({ response }) {
      isLoading.value = false;
      if (response._data?.data?.token) {
        setToken(response._data?.data?.token);
        await getSession();

        if (isMini) {
          emits("onClose");
          return;
        }

        if (route.query?.callback) {
          navigateTo(route.query.callback);
          return;
        }

        // redirec to last visited page
        const cookie = useCookie("lastVisitedPage");
        const lastVisitedPage = cookie.value;
        let redirectTo = "/explore";

        if (lastVisitedPage && lastVisitedPage.path) {
          const path = lastVisitedPage.path;
          const query = lastVisitedPage.query
            ? { ...lastVisitedPage.query }
            : {};

          redirectTo = { path, query };
        }

        cookie.value = null;
        await navigateTo(redirectTo);
      }
    },
    onResponseError() {
      isInvalid.value = true;
      isLoading.value = false;
    },
  });
};

const loginWithGoogle = () => {
  socialLoginLoading.value = true;
  navigateTo(config.public.apiBaseUrl + "auth/google/redirect", {
    external: true,
  });
};

const userEmail = ref(null);
const forgotVisible = ref(false);

const onForgotPassword = async () => {
  await useApiFetch("auth/password/forgot", {
    method: "POST",
    body: {
      email: userEmail.value,
    },
    onResponse({ response }) {
      console.log(response);
      if ([200, 201].includes(response.status)) {
        toast.add({
          severity: "success",
          summary: `An email has been sent to ${userEmail.value}.`,
          life: 4000,
        });
        navigateTo("/reset-password?email=" + userEmail.value);
      }
    },
    onResponseError() {
      toast.add({
        severity: "error",
        summary: "Sorry! Could not find that email address.",
        life: 3000,
      });
    },
  });
  forgotVisible.value = false;
  userEmail.value = null;
};

const handleCreateAccount = () => {
  if (isMini) {
    emits("signup");
  } else {
    navigateTo("/signup");
  }
};
</script>

<template>
  <div class="space-y-0">
    <p v-if="isMini" class="font-bold text-slate-800">Login account</p>
    <h1
      v-else
      class="text-[28px] lg:text-[36px] xl:text-[40px] font-bold text-primary-500 leading-[1] mb-3"
    >
      Student Account
    </h1>

    <p class="text-sm font-normal text-[#4B5563]">
      If you don’t have an account,
      <span
        @click="handleCreateAccount"
        class="font-bold text-blue-500 cursor-pointer hover:underline"
      >
        Create new account
      </span>
    </p>
  </div>

  <Button
    label="Login with Google"
    severity="secondary"
    icon="pi pi-google"
    outlined
    class="text-sm lg:text-lg !border !border-[#64748B] py-1.5 lg:py-2 leading-normal min-h-[44px]"
    :class="{
      '!text-sm': isMini,
    }"
    :size="isMini ? 'small' : null"
    :loading="socialLoginLoading"
    @click.prevent="loginWithGoogle"
  >
    <template #icon>
      <img src="~/assets/img/icon/google.svg" class="h-5 w-5" />
    </template>
  </Button>

  <Divider align="center" type="solid" class="w-full">
    <b class="text-sm text-[#9E9E9E] font-normal">or continue with</b>
  </Divider>

  <form
    class="w-full flex flex-col gap-6 justify-between"
    @submit.prevent="handleLogin"
  >
    <div class="space-y-4 lg:space-y-6">
      <Message
        v-if="isInvalid"
        severity="error"
        :pt="{
          text: 'font-normal',
        }"
      >
        Username or password is invalid.
      </Message>

      <div class="flex flex-col gap-2">
        <label
          class="text-sm lg:text-base font-semibold text-[#212121]"
          for="email"
          >Email address</label
        >
        <InputText
          id="email"
          v-model="email"
          placeholder="example@gmail.com"
          class="w-full placeholder:text-sm lg:placeholder:text-base text-[#4B5563] text-sm lg:text-base py-2 px-3 min-h-[40px] rounded-md"
          aria-describedby="email-address-help"
          :invalid="isInvalid"
        />
        <!-- <small id="email-address-help"
            >Enter your username to reset your password.</small
            > -->
      </div>

      <div class="flex flex-col gap-2">
        <label
          class="text-sm lg:text-base font-semibold text-[#212121]"
          for="password"
          >Password</label
        >
        <Password
          inputClass="w-full placeholder:text-sm lg:placeholder:text-base text-[#4B5563] text-sm lg:text-base py-2 px-3 min-h-[40px] rounded-md"
          id="password"
          v-model="password"
          placeholder="Enter password"
          toggleMask
          :feedback="false"
          :invalid="isInvalid"
        />

        <Button
          link
          size="sm"
          class="text-xs lg:text-sm font-semibold text-blue-500 cursor-pointer w-fit"
          @click="forgotVisible = true"
        >
          Forgot password?
        </Button>
      </div>
    </div>

    <div class="flex justify-end gap-2">
      <Button
        v-if="isMini"
        class="w-full text-xs lg:text-sm self-end mt-3 min-h-[40px]"
        :class="{
          'lg:w-[180px]': !isMini,
        }"
        type="button"
        label="Maybe later"
        fluid
        outlined
        @click="emits('onClose')"
      ></Button>
      <Button
        :loading="isLoading"
        type="submit"
        label="Sign in"
        class="w-full text-xs lg:text-sm self-end mt-3 min-h-[40px]"
        :class="{
          'lg:w-[180px]': !isMini,
        }"
      />
    </div>
  </form>

  <Dialog
    v-model:visible="forgotVisible"
    modal
    header="Forgot Password"
    :style="{ width: '25rem' }"
  >
    <div class="flex gap-2 items-center">
      <p>Remember your password?</p>
      <Button
        link
        size="sm"
        class="text-xs lg:text-sm font-semibold text-blue-500 cursor-pointer w-fit"
        @click="forgotVisible = false"
      >
        Login here
      </Button>
    </div>
    <div class="flex flex-col items-start gap-1 my-8">
      <label for="email" class="font-semibold">Email address</label>
      <InputText
        v-model="userEmail"
        id="email"
        class="w-full"
        autocomplete="off"
        fluid
      />
    </div>
    <div class="flex justify-end gap-2">
      <Button
        class="w-full"
        type="button"
        label="Reset password"
        fluid
        @click="onForgotPassword"
        :disabled="!isValidEmail(userEmail)"
      ></Button>
    </div>
  </Dialog>
</template>
